/**
 * 渠道结��健康诊断 - Vuex Store
 * 管理诊断全流程状态
 */
import {
  DIMENSIONS, DEFAULT_SCORES, AI_CONFIDENCE, RECOMMENDATIONS,
  PHASE1_ACTIONS, PHASE2_ACTIONS, getGrade, getGaugeColor
} from '../utils/constants'

export default {
  namespaced: true,

  state: () => ({
    // Tab 状态
    activeTab: 'ai',

    // AI 诊断状态
    aiMode: false,
    aiBrand: '',
    aiIndustry: '',
    diagnosisStatus: 'idle', // idle | loading | completed

    // 评分数据
    scores: {},           // { qid: score }
    brmSource: {},        // { qid: true } — BRM来源标记

    // BRM & AI 数据
    brmData: null,        // BRM品牌数据
    aiData: null,         // AI搜索结果

    // 诊断结果
    reportData: null,     // 报告数据
    recData: null,        // 优化建议

    // 后端会话ID
    sessionId: null
  }),

  getters: {
    // 是否全部18题已评分
    allScored: state => {
      const all = DIMENSIONS.flatMap(d => d.questions.map(q => q.id))
      return all.every(id => id in state.scores)
    },

    // 剩余未评题目数
    remainingCount: state => {
      const all = DIMENSIONS.flatMap(d => d.questions.map(q => q.id))
      return all.length - Object.keys(state.scores).length
    },

    // 各维度完成题目数
    dimensionProgress: state => dimId => {
      const dim = DIMENSIONS.find(d => d.id === dimId)
      if (!dim) return 0
      return dim.questions.filter(q => q.id in state.scores).length
    },

    // 计算六维得分
    dimensionScores: state => {
      return DIMENSIONS.map(d => {
        const raw = d.questions.reduce((a, q) => a + (state.scores[q.id] || 0), 0) / d.questions.length
        const score = Math.round(((raw - 1) / 4) * 100)
        return {
          id: d.id,
          name: d.name,
          weight: d.weight,
          theory: d.theory,
          harmMessage: d.harmMessage,
          raw: raw,
          score: score,
          color: getGaugeColor(score),
          details: d.questions.map(q => ({
            id: q.id,
            score: state.scores[q.id] || 0,
            confidence: AI_CONFIDENCE[q.id] || 'low'
          }))
        }
      })
    },

    // 健康指数
    totalScore: (state, getters) => {
      const ds = getters.dimensionScores
      const tw = ds.reduce((a, d) => a + d.weight, 0)
      const ws = ds.reduce((a, d) => a + d.score * d.weight, 0)
      return Math.round(ws / tw)
    },

    // 诊断等级
    gradeInfo: (state, getters) => getGrade(getters.totalScore),

    // 优化建议列表
    recommendations: (state, getters) => {
      const ds = getters.dimensionScores
      const sorted = [...ds].sort((a, b) => a.score - b.score)
      const recs = []

      sorted.forEach(d => {
        const lvl = d.score < 50 ? 'urgent' : d.score < 70 ? 'improve' : 'optimize'
        const r = RECOMMENDATIONS[d.id] && RECOMMENDATIONS[d.id][lvl]
        if (r) {
          recs.push({
            priority: lvl,
            dimensionName: d.name,
            dimensionId: d.id,
            score: d.score,
            title: r.title,
            desc: r.desc
          })
        }
      })

      if (!recs.length && getters.totalScore >= 85) {
        recs.push({
          priority: 'optimize',
          dimensionName: '整体',
          score: getters.totalScore,
          title: '渠道结构已达优秀水平',
          desc: '六个维度均表现良好。建议持续关注行业趋势，每季度复盘渠道结构演进情况，保持结构竞争力。'
        })
      }
      return recs
    },

    // 分期策略
    phaseStrategies: (state, getters) => {
      const ds = getters.dimensionScores
      const low = ds.filter(d => d.score < 50)
      const mid = ds.filter(d => d.score >= 50 && d.score < 70)

      return {
        phase1: low.length
          ? low.map(d => ({ name: d.name, score: d.score, action: PHASE1_ACTIONS[d.id] || '制定针对性改善计划' }))
          : [{ name: '各项指标', score: null, action: '均在安全线以上，无需紧急干预' }],
        phase2: mid.length
          ? mid.map(d => ({ name: d.name, score: d.score, action: PHASE2_ACTIONS[d.id] || '持续推进结构优化' }))
          : [{ name: '整体', score: null, action: '推进渠道结构精细化，将各维度提升至85分以上' }]
      }
    }
  },

  mutations: {
    SET_ACTIVE_TAB(state, tab) { state.activeTab = tab },
    SET_AI_MODE(state, val) { state.aiMode = val },
    SET_AI_BRAND(state, val) { state.aiBrand = val },
    SET_AI_INDUSTRY(state, val) { state.aiIndustry = val },
    SET_DIAGNOSIS_STATUS(state, val) { state.diagnosisStatus = val },
    SET_SCORE(state, { qid, score }) { state.scores = { ...state.scores, [qid]: score } },
    SET_BRM_SOURCE(state, { qid, val }) { state.brmSource = { ...state.brmSource, [qid]: val } },
    CLEAR_BRM_SOURCE(state, qid) {
      const b = { ...state.brmSource }; delete b[qid]; state.brmSource = b
    },
    SET_BRM_DATA(state, data) { state.brmData = data },
    SET_AI_DATA(state, data) { state.aiData = data },
    SET_REPORT_DATA(state, data) { state.reportData = data },
    SET_REC_DATA(state, data) { state.recData = data },
    SET_SESSION_ID(state, id) { state.sessionId = id },

    // 批量设置评分（AI诊断返回）
    SET_BATCH_SCORES(state, { scores, brmSource }) {
      state.scores = { ...scores }
      state.brmSource = { ...brmSource }
    },

    // 重置所有
    RESET_ALL(state) {
      state.aiMode = false
      state.aiBrand = ''
      state.aiIndustry = ''
      state.diagnosisStatus = 'idle'
      state.scores = {}
      state.brmSource = {}
      state.brmData = null
      state.aiData = null
      state.reportData = null
      state.recData = null
      state.sessionId = null
    }
  },

  actions: {
    // 切换Tab
    switchTab({ commit }, tab) { commit('SET_ACTIVE_TAB', tab) },

    // 开始AI诊断
    async startDiagnosis({ commit, dispatch }, { brandName, industry }) {
      commit('SET_AI_MODE', true)
      commit('SET_AI_BRAND', brandName)
      commit('SET_AI_INDUSTRY', industry)
      commit('SET_DIAGNOSIS_STATUS', 'loading')

      try {
        // TODO: 对接后端API
        // const res = await dispatch('api/aiSearch', { brandName, industry }, { root: true })
        // 当前使用前端静态数据模拟
        commit('SET_DIAGNOSIS_STATUS', 'completed')
      } catch (err) {
        commit('SET_DIAGNOSIS_STATUS', 'idle')
        throw err
      }
    },

    // 单个评分
    setScore({ commit }, { qid, score, isBrm }) {
      commit('SET_SCORE', { qid, score })
      if (isBrm) {
        commit('SET_BRM_SOURCE', { qid, val: true })
      } else {
        commit('CLEAR_BRM_SOURCE', qid)
      }
    },

    // 从后端加载历史诊断
    async loadHistory({ commit }, sessionId) {
      // TODO: 对接后端 API GET /api/diagnosis/report/{sessionId}
      commit('SET_SESSION_ID', sessionId)
    },

    // 保存诊断到后端
    async saveDiagnosis({ state, getters }) {
      // TODO: 对接后端 API POST /api/diagnosis/save
      const payload = {
        brandName: state.aiBrand,
        industry: state.aiIndustry,
        totalScore: getters.totalScore,
        grade: getters.gradeInfo.grade,
        gradeCode: getters.gradeInfo.code,
        scores: state.scores,
        brmSource: state.brmSource,
        brmData: state.brmData,
        aiData: state.aiData,
        dimensions: getters.dimensionScores,
        recommendations: getters.recommendations
      }
      // const res = await api.saveDiagnosis(payload)
      // commit('SET_SESSION_ID', res.sessionId)
      return payload
    },

    resetAll({ commit }) { commit('RESET_ALL') }
  }
}
